import type { ExperienceLevel } from "@/schemas/onboarding";

export type { RoleMeta } from "@/constants/roles";
export {
  FOOD_SERVICE_ROLES as ROLE_GROUPS,
  ROLE_NAME,
} from "@/constants/roles";

export const EXP_LEVELS: Array<[value: ExperienceLevel, label: string]> = [
  ["none", "New to it"],
  ["lt1", "< 1 year"],
  ["1to3", "1-3 years"],
  ["gt3", "3+ years"],
];

export const EXP_NAME: Record<ExperienceLevel, string> = Object.fromEntries(
  EXP_LEVELS,
) as Record<ExperienceLevel, string>;

export interface WorkExperience {
  restaurant: string;
  start: string;
  end: string;
  current: boolean;
  roles: Array<string>;
}

export interface WorkerOnboardingData {
  name: string;
  email: string;
  workExperiences: Array<WorkExperience>;
  /** User explicitly said they've never worked at a restaurant — skip validation. */
  noExperience: boolean;
  city: string;
  radius: number;
  latitude?: number;
  longitude?: number;
  photoUrl?: string;
  resumeUrl?: string;
}

export function emptyWorkExperience(): WorkExperience {
  return { restaurant: "", start: "", end: "", current: false, roles: [] };
}

export function isWorkExperienceComplete(e: WorkExperience): boolean {
  return Boolean(
    e.restaurant.trim() &&
      e.start &&
      (e.current || e.end) &&
      e.roles.length > 0,
  );
}

function parseYearMonth(s: string): { y: number; m: number } | undefined {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(s);
  if (!match) return undefined;
  return { y: Number(match[1]), m: Number(match[2]) };
}

function monthsBetween(start: string, end: string): number {
  const s = parseYearMonth(start);
  const e = parseYearMonth(end);
  if (!s || !e) return 0;
  return Math.max(0, (e.y - s.y) * 12 + (e.m - s.m) + 1);
}

/**
 * Maps a single work-experience date range to the BE's required level enum.
 * "current" treats today as the end month so an open-ended stint still buckets.
 */
export function levelForDuration(
  start: string,
  end: string,
  current: boolean,
): Exclude<ExperienceLevel, "none"> {
  let endRef = end;
  if (current) {
    const now = new Date();
    endRef = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }
  const months = monthsBetween(start, endRef);
  if (months <= 12) return "lt1";
  if (months <= 36) return "1to3";
  return "gt3";
}
